const sensitive = /password|passwd|(^|_)pin($|_)|token|secret|credential|authorization|api_?key/i;
export function redactLogDetails(value, depth = 0) {
  if (depth > 12) return '[Nested detail omitted]';
  if (Array.isArray(value)) return value.map(item => redactLogDetails(item, depth + 1));
  if (value && typeof value === 'object') return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, sensitive.test(key.replace(/([a-z])([A-Z])/g, '$1_$2').replace(/[\s-]/g, '_')) ? '[Hidden]' : redactLogDetails(item, depth + 1)]));
  if (typeof value === 'string') {
    if (/^[\[{]/.test(value.trim())) { try { return redactLogDetails(JSON.parse(value), depth + 1); } catch {} }
    return value.replace(/((?:password|passwd|pin|token|secret|api_key|authorization)\s*[=:]\s*)[^\s&,;"}]+/gi, '$1[Hidden]');
  }
  return value;
}
export const canonicalLogAction = action => action === 'ADMIN_LOGIN' ? 'ADMIN_LOGIN_SUCCESS' : action || 'UNKNOWN_EVENT';
export function logActionLabel(action) {
  if (/LOGIN_AMBIGUOUS/.test(action)) return 'Sign-in could not be verified';
  if (/LOGIN_FAILED/.test(action)) return 'Sign-in failed';
  if (/LOGIN_SUCCESS|^ADMIN_LOGIN$/.test(action)) return 'Signed in';
  if (/LOGOUT/.test(action)) return 'Signed out';
  if (/UNLOCK/.test(action)) return 'Account unlocked';
  if (/LOCKED/.test(action)) return 'Account locked';
  if (/PIN_RESET/.test(action)) return 'Employee PIN reset';
  if (/CHANGE_PIN/.test(action)) return 'Employee PIN changed';
  return String(action || 'Unknown event').toLowerCase().replace(/_/g, ' ').replace(/^./, char => char.toUpperCase());
}
export function logOutcome(action) {
  if (/UNLOCK/.test(action)) return 'unlocked';
  if (/LOCKED/.test(action)) return 'locked';
  if (/FAILED|ERROR|LOGIN_AMBIGUOUS/.test(action)) return 'failed';
  if (/LOGIN_SUCCESS|^ADMIN_LOGIN$/.test(action)) return 'success';
  return 'recorded';
}
export function logActorRole(log) {
  const type = String(log.actor_type || '').toLowerCase().replace(/ /g, '_');
  const username = log.details?.admin_username || log.details?.username;
  if (type === 'super_admin' || log.details?.role === 'super_admin' || /^SUPER_ADMIN_/.test(log.action || '') || (type === 'admin' && !log.company_id && String(username).toLowerCase() === 'admin')) return 'Super Admin';
  if (type === 'employee') return 'Employee';
  if (type === 'admin' || type === 'company') return log.company_id ? 'Company Admin' : 'Administrator';
  return type === 'system' ? 'System' : 'Unknown';
}
export function logActorName(log) {
  const role = logActorRole(log);
  if (logOutcome(log.action) === 'failed' && /LOGIN/.test(log.action || '')) return 'Not verified';
  if (/UNLOCK/.test(log.action || '')) return log.details?.unlocked_by || log.details?.admin_username || '—';
  if (role === 'Employee') return log.employees?.name || log.details?.actor_name || log.details?.employee_name || log.details?.pan || '—';
  return log.details?.admin_username || log.details?.actor_name || log.details?.username || '—';
}
export function logTarget(log) {
  const employee = log.employees;
  if (employee?.name) return employee.employee_code ? `${employee.name} (${employee.employee_code})` : employee.name;
  return log.details?.employee_name || log.details?.name || log.details?.employee_code || log.details?.pan || log.details?.username || log.details?.employee_id || log.employee_id || '—';
}
export function logTime(value) {
  const date = new Date(value);
  return !value || Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(date);
}
export function logDay(value) {
  const date = new Date(value);
  if (!value || Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
}
// Only pair the known legacy ADMIN_LOGIN + ADMIN_LOGIN_SUCCESS mirror, one-to-one.
// Repeated failures, successes and business actions remain separate events.
export function prepareLogRows(logs = []) {
  const seen = new Set();
  const rows = logs.filter(log => {
    if (!log.id) return true;
    const key = `${log.source || 'audit'}:${log.id}`;
    if (seen.has(key)) return false;
    seen.add(key); return true;
  }).map((log, index) => ({ ...log, _key: `${log.source || 'audit'}:${log.id || index}`, sourceRows: log.sourceRows || [log], details: redactLogDetails(log.details || {}) }));
  const paired = new Set();
  rows.filter(log => log.action === 'ADMIN_LOGIN').forEach(legacy => {
    const username = legacy.details.admin_username || legacy.details.username;
    if (!username || !legacy.created_at) return;
    const matches = rows.filter(candidate => candidate.action === 'ADMIN_LOGIN_SUCCESS' && !paired.has(candidate._key) &&
      (candidate.company_id || null) === (legacy.company_id || null) &&
      String(candidate.details.admin_username || candidate.details.username || '').toLowerCase() === String(username).toLowerCase() &&
      Math.abs(new Date(candidate.created_at) - new Date(legacy.created_at)) <= 5000)
      .sort((a, b) => Math.abs(new Date(a.created_at) - new Date(legacy.created_at)) - Math.abs(new Date(b.created_at) - new Date(legacy.created_at)));
    if (!matches.length) return;
    const canonical = matches[0];
    canonical.details = { ...legacy.details, ...canonical.details };
    canonical.sourceRows.push(...legacy.sourceRows);
    paired.add(canonical._key); paired.add(legacy._key); legacy._hidden = true;
  });
  return rows.filter(log => !log._hidden).sort((a, b) => (Date.parse(b.created_at) || 0) - (Date.parse(a.created_at) || 0));
}
export function filterLogRows(rows, { search = '', outcome = 'all', role = 'all', from = '', to = '' } = {}, companies = []) {
  const names = new Map(companies.map(company => [company.id, company.name]));
  const query = search.trim().toLowerCase();
  return rows.filter(log => {
    const day = logDay(log.created_at);
    if (outcome !== 'all' && logOutcome(log.action) !== outcome) return false;
    if (role !== 'all' && logActorRole(log) !== role) return false;
    if ((from && (!day || day < from)) || (to && (!day || day > to))) return false;
    const text = [log.action, logActionLabel(log.action), logActorRole(log), logActorName(log), logTarget(log), names.get(log.company_id), JSON.stringify(log.details)].join(' ').toLowerCase();
    return !query || text.includes(query);
  });
}
export function detailPairs(details) {
  return Object.entries(details || {}).filter(([key, value]) => !['timestamp','admin_username','company_id'].includes(key) && value !== undefined && value !== null && value !== '')
    .map(([key, value]) => [key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/^./, char => char.toUpperCase()).replace(/\bpin\b/gi, 'PIN').replace(/\bid\b/gi, 'ID'), typeof value === 'boolean' ? (value ? 'Yes' : 'No') : typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)]);
}
export function exportLogText(rows, companies = [], title = 'Activity log') {
  const names = new Map(companies.map(company => [company.id, company.name]));
  return [`TrustWork · ${title}`, `Exported: ${logTime(new Date())} IST`, `${rows.length} matching loaded events`, '', ...rows.map((row, index) => [
    `${index + 1}. ${logTime(row.created_at)} IST · ${logActionLabel(row.action)}`,
    `Company: ${row.company_id ? names.get(row.company_id) || row.company_id : 'Platform'}`,
    `Actor: ${logActorRole(row)} · ${logActorName(row)}`, `Target: ${logTarget(row)}`,
    ...detailPairs(redactLogDetails(row.details)).map(([key, value]) => `${key}: ${value}`),
    `Source records: ${row.sourceRows.map(source => `${source.source || 'audit'}:${source.source_id || source.id || 'unavailable'} (${source.action})`).join(', ')}`, ''
  ].join('\n'))].join('\n');
}
